import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtErr, API } from "../../lib/api";
import { PageHeader, Modal, inputCls, btnPrimary, btnTurq, btnGhost, EmptyState } from "../../components/bits";
import { Plus, Upload, Download, FileSpreadsheet, Pencil, Trash2, Search, Sprout, Target, Crown, Clock3, Users } from "lucide-react";

const GRADE_SECTIONS = [
  { key: "أول ثانوي", icon: Sprout, badge: "براعم العطاء",
    phrase: "أول خطوة في رحلة البذل… حيث يولد الوعي التطوعي وتُصنع أول بصمة خير" },
  { key: "ثاني ثانوي", icon: Target, badge: "صنّاع الأثر",
    phrase: "عطاءٌ يتعمّق ويترسّخ… حتى يصبح التطوع عادةً وأسلوب حياة" },
  { key: "ثالث ثانوي", icon: Crown, badge: "قادة الغد",
    phrase: "يختمون مسيرتهم المدرسية بإرثٍ من البذل… يلهم من بعدهم" },
];
const GRADE_KEYS = GRADE_SECTIONS.map((s) => s.key);
const OTHER_KEY = "أخرى";
const SECTIONS = ["١", "٢", "٣", "٤", "٥", "٦"];
const GRADE_PREFIX = { "أول ثانوي": "أولى", "ثاني ثانوي": "ثانية", "ثالث ثانوي": "ثالثة" };

const normalizeGrade = (g) => (g || "")
  .replace("الأول", "أول").replace("الثاني", "ثاني").replace("الثالث", "ثالث")
  .replace("الثانوي", "ثانوي").trim();

const emptyForm = { name: "", grade: "أول ثانوي", section: "" };

function GradeSection({ sec, idx, items, girls, onEdit, onRemove }) {
  const Icon = sec.icon;
  const totalHours = items.reduce((a, s) => a + (Number(s.hours) || 0), 0);
  return (
    <div data-testid={`grade-section-${idx}`} className="bg-surface rounded-2xl shadow-md border border-brd/50 overflow-hidden">
      <div className="bg-turq-900 text-white px-5 py-4 flex items-center gap-3 flex-wrap">
        <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-head font-black text-lg">{sec.key}</h3>
            <span data-testid={`grade-badge-${idx}`} className="text-[10px] font-bold bg-coral-600 rounded-full px-2.5 py-0.5">{sec.badge}</span>
          </div>
          <p className="text-[11px] text-white/75 mt-1 leading-relaxed">{sec.phrase}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <span data-testid={`grade-count-${idx}`} className="text-[11px] font-bold bg-white/15 rounded-full px-3 py-1.5 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> {items.length} {girls ? "طالبة" : "طالب"}
          </span>
          <span data-testid={`grade-hours-${idx}`} className="text-[11px] font-bold bg-white/15 rounded-full px-3 py-1.5 flex items-center gap-1.5">
            <Clock3 className="w-3.5 h-3.5" /> {totalHours} ساعة
          </span>
        </div>
      </div>
      {items.length === 0 ? (
        <div data-testid={`grade-empty-${idx}`} className="px-5 py-6 text-center text-xs text-mutext">
          لا توجد سجلات في هذه المرحلة بعد — أضف {girls ? "طالبة" : "طالباً"} أو استورد ملف Excel
        </div>
      ) : (
        <div className="overflow-auto">
          <table className="w-full text-sm" data-testid={`grade-table-${idx}`}>
            <thead>
              <tr className="bg-bg text-mutext">
                <th className="text-right px-5 py-2.5 font-head text-[11px]">الاسم الرباعي</th>
                <th className="text-right px-5 py-2.5 font-head text-[11px]">عدد الساعات التطوعية (من الفرص)</th>
                <th className="text-right px-5 py-2.5 font-head text-[11px]">الفرص</th>
                <th className="text-right px-5 py-2.5 font-head text-[11px]">الشهادة</th>
                <th className="text-right px-5 py-2.5 font-head text-[11px]">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} data-testid={`student-row-${s.id.slice(0, 8)}`} className="border-b border-brd/50 last:border-0 hover:bg-bg transition-colors">
                  <td className="px-5 py-3 font-bold">{s.name}
                    {s.section && <span data-testid={`student-section-${s.id.slice(0, 8)}`} className="mr-2 text-[10px] font-bold bg-beige-600 dark:bg-surface2 text-turq-900 dark:text-turq-300 rounded-full px-2 py-0.5">{GRADE_PREFIX[normalizeGrade(s.grade)] || "شعبة"} {s.section}</span>}
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-[11px] font-bold bg-coral-100 text-coral-900 dark:bg-surface2 dark:text-coral-300 rounded-full px-3 py-1">
                      {s.hours || 0} ساعة
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-[11px] font-bold bg-turq-100 text-turq-900 dark:bg-surface2 dark:text-turq-300 rounded-full px-3 py-1">
                      {s.participations} فرصة
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    {s.certificate ? (
                      <a data-testid={`student-cert-badge-${s.id.slice(0, 8)}`} target="_blank" rel="noreferrer"
                        href={`${API}/portal/students/${s.id}/certificate`}
                        className="text-[11px] font-bold bg-turq-600 text-white rounded-full px-3 py-1 hover:bg-turq-900 transition-colors">معقودة — عرض</a>
                    ) : (
                      <span data-testid={`student-cert-badge-${s.id.slice(0, 8)}`} className="text-[11px] font-bold bg-coral-100 text-coral-900 dark:bg-surface2 dark:text-coral-300 rounded-full px-3 py-1">مفقودة</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex gap-2">
                      <button data-testid={`student-edit-${s.id.slice(0, 8)}`} onClick={() => onEdit(s)}
                        className="w-8 h-8 rounded-lg bg-beige-600 dark:bg-surface2 flex items-center justify-center hover:scale-105 transition-transform">
                        <Pencil className="w-3.5 h-3.5 text-turq-900 dark:text-turq-300" />
                      </button>
                      <button data-testid={`student-delete-${s.id.slice(0, 8)}`} onClick={() => onRemove(s)}
                        className="w-8 h-8 rounded-lg bg-coral-100 dark:bg-surface2 flex items-center justify-center hover:scale-105 transition-transform">
                        <Trash2 className="w-3.5 h-3.5 text-coral-900 dark:text-coral-300" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function studentModalTitle(mode, girls) {
  if (mode === "edit") return girls ? "تعديل بيانات الطالبة" : "تعديل بيانات الطالب";
  return girls ? "إضافة طالبة متطوعة" : "إضافة طالب متطوع";
}

export default function Students() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [girls, setGirls] = useState(false);
  const [importGrade, setImportGrade] = useState("أول ثانوي");
  const [importSection, setImportSection] = useState("");
  const [certFile, setCertFile] = useState(null);
  const fileRef = useRef(null);
  const location = useLocation();
  const gradeOnly = location.hash.startsWith("#grade-") ? normalizeGrade(decodeURIComponent(location.hash.slice(7))) : "";

  useEffect(() => {
    api.get("/portal/settings").then((r) => setGirls(r.data.school?.gender === "بنات")).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    api.get(`/portal/students?${params}`).then((r) => setStudents(r.data)).catch((e) => toast.error(fmtErr(e)))
      .finally(() => setLoading(false));
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const payload = { name: form.name.trim(), grade: form.grade, section: form.section || "" };
    if (!payload.name) {
      toast.error("أدخل الاسم الرباعي");
      return;
    }
    setBusy(true);
    try {
      let sid = modal?.student?.id;
      if (modal?.mode === "edit") {
        await api.put(`/portal/students/${modal.student.id}`, payload);
        toast.success(girls ? "تم تحديث بيانات الطالبة" : "تم تحديث بيانات الطالب");
      } else {
        const { data: created } = await api.post("/portal/students", payload);
        sid = created.id || sid;
        toast.success(girls ? "تمت إضافة الطالبة بنجاح" : "تمت إضافة الطالب بنجاح");
      }
      if (certFile && sid) {
        const fd = new FormData();
        fd.append("file", certFile);
        await api.post(`/portal/students/${sid}/certificate`, fd);
        toast.success("تم رفع شهادة الساعات");
      }
      setCertFile(null);
      setModal(null);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`حذف «${s.name}» وجميع مشاركاته؟`)) return;
    try {
      await api.delete(`/portal/students/${s.id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const doImport = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) {
      toast.error("اختر ملف Excel أولاً");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("grade", importGrade);
      fd.append("default_section", importSection);
      const { data } = await api.post("/portal/students/import", fd);
      setReport(data);
      setModal(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const groups = {};
  GRADE_KEYS.forEach((k) => { groups[k] = []; });
  groups[OTHER_KEY] = [];
  students.forEach((s) => {
    const k = normalizeGrade(s.grade);
    groups[GRADE_KEYS.includes(k) ? k : OTHER_KEY].push(s);
  });

  return (
    <div data-testid="students-page">
      <PageHeader title={gradeOnly ? (girls ? `طالبات ${gradeOnly}` : `طلاب ${gradeOnly}`) : (girls ? "الطالبات المتطوعات" : "الطلاب المتطوعون")}
        sub={gradeOnly ? `سجل ${gradeOnly} التفصيلي — الاسم الرباعي والساعات والشهادة` : "سجلات المتطوعين مقسّمة على مراحل الثانوية الثلاث — الاسم الرباعي أساس السجل والساعات تُحتسب من الفرص فقط"}>
        {gradeOnly && (
          <Link data-testid="students-show-all" to="/portal/students" className={btnGhost}>عرض كل المراحل</Link>
        )}
        <button data-testid="student-add-btn" onClick={() => { setForm(emptyForm); setModal({ mode: "add" }); }} className={btnPrimary}>
          <Plus className="w-4 h-4" /> {girls ? "إضافة طالبة" : "إضافة طالب"}
        </button>
        <button data-testid="excel-import-btn" onClick={() => setModal({ mode: "import" })} className={btnTurq}>
          <Upload className="w-4 h-4" /> استيراد Excel
        </button>
        <button data-testid="excel-template-btn" onClick={() => window.open(`${API}/portal/students/template`, "_blank")} className={btnGhost}>
          <FileSpreadsheet className="w-4 h-4" /> القالب
        </button>
        <button data-testid="excel-export-btn" onClick={() => window.open(`${API}/portal/students/export`, "_blank")} className={btnGhost}>
          <Download className="w-4 h-4" /> تصدير
        </button>
      </PageHeader>

      <div className="flex gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute right-3 top-3 text-mutext" />
          <input data-testid="student-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()} placeholder="ابحث بالاسم ثم Enter" className={`${inputCls} pr-9`} />
        </div>
      </div>

      {loading ? (
        <div className="text-center text-mutext py-16 font-head">جارٍ التحميل …</div>
      ) : students.length === 0 ? (
        <EmptyState testid="students-empty" text="لا يوجد متطوعون بعد — أضفهم يدوياً أو عبر استيراد Excel" />
      ) : (
        <div className="space-y-6">
          {GRADE_SECTIONS.filter((sec) => !gradeOnly || sec.key === gradeOnly).map((sec, i) => (
            <GradeSection key={sec.key} sec={sec} idx={i} items={groups[sec.key]} girls={girls}
              onEdit={(s) => { setForm({ name: s.name, grade: GRADE_KEYS.includes(normalizeGrade(s.grade)) ? normalizeGrade(s.grade) : "أول ثانوي", section: s.section || "" }); setCertFile(null); setModal({ mode: "edit", student: s }); }}
              onRemove={remove} />
          ))}
          {groups[OTHER_KEY].length > 0 && !gradeOnly && (
            <GradeSection sec={{ key: "سجلات أخرى", icon: Users, badge: "خارج المراحل الثلاث", phrase: "سجلات مسجلة بصفوف أخرى — يمكنك تعديلها لتُصنَّف ضمن المراحل الثانوية" }}
              idx="other" items={groups[OTHER_KEY]} girls={girls}
              onEdit={(s) => { setForm({ name: s.name, grade: "أول ثانوي", section: s.section || "" }); setCertFile(null); setModal({ mode: "edit", student: s }); }}
              onRemove={remove} />
          )}
        </div>
      )}

      <Modal open={modal?.mode === "add" || modal?.mode === "edit"} onClose={() => setModal(null)}
        title={studentModalTitle(modal?.mode, girls)} testid="student-form-modal">
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">الاسم الرباعي *</label>
            <input data-testid="student-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">المرحلة *</label>
            <select data-testid="student-grade-select" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} className={inputCls}>
              {GRADE_KEYS.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">الشعبة (اختياري — تسهّل الاختيار في المدارس الكبيرة)</label>
            <select data-testid="student-section-select" value={form.section || ""} onChange={(e) => setForm({ ...form, section: e.target.value })} className={inputCls}>
              <option value="">— بدون شعبة —</option>
              {SECTIONS.map((sc) => <option key={sc} value={sc}>{GRADE_PREFIX[form.grade] || form.grade} {sc}</option>)}
            </select>
          </div>
          <p data-testid="student-hours-note" className="text-[11px] text-mutext bg-turq-100/60 dark:bg-surface2 rounded-xl px-3 py-2 leading-relaxed">
            الساعات التطوعية تُحتسب تلقائياً من مشاركة الطالب في الفرص فقط — لا تُدخل يدوياً.
          </p>
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">صورة شهادة المتطوع بالساعات من منصة التطوع (اختياري)</label>
            <input data-testid="student-cert-input" type="file" accept="image/*,application/pdf" className={inputCls}
              onChange={(e) => setCertFile(e.target.files?.[0] || null)} />
            {modal?.student?.certificate && (
              <a data-testid="student-cert-view" target="_blank" rel="noreferrer"
                href={`${API}/portal/students/${modal.student.id}/certificate`}
                className="text-[11px] font-bold text-turq-600 hover:text-turq-900 mt-1 inline-block">عرض الشهادة الحالية</a>
            )}
          </div>
          <button data-testid="student-save-btn" onClick={save} disabled={busy} className={`${btnPrimary} w-full justify-center`}>
            {busy ? "جارٍ الحفظ …" : "حفظ"}
          </button>
        </div>
      </Modal>

      <Modal open={modal?.mode === "import"} onClose={() => setModal(null)} title="استيراد المتطوعين من Excel" testid="import-modal">
        <div className="space-y-4">
          <p className="text-xs text-mutext leading-relaxed">
            القالب: <b>الاسم الرباعي</b> + <b>المرحلة</b> (اختياري لكل صف: أول/ثاني/ثالث ثانوي) + <b>الشعبة</b> (١ إلى ٦ — اختياري).
            يمكن استيراد كل المراحل والشُعب في ملف واحد، والصف بلا مرحلة أو شعبة يُسند للمختار أدناه. الساعات تُحتسب من الفرص فقط والتكرار يُكتشف تلقائياً.
          </p>
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">المرحلة الافتراضية (للصفوف بلا عمود مرحلة) *</label>
            <select data-testid="import-grade-select" value={importGrade} onChange={(e) => { setImportGrade(e.target.value); setImportSection(""); }} className={inputCls}>
              {GRADE_KEYS.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">الشعبة الافتراضية (تنبثق من المرحلة — لاستيراد كل شعبة على حدة)</label>
            <select data-testid="import-section-select" value={importSection} onChange={(e) => setImportSection(e.target.value)} className={inputCls}>
              <option value="">— كل الشعب / بدون شعبة —</option>
              {SECTIONS.map((sc) => <option key={sc} value={sc}>{GRADE_PREFIX[importGrade] || importGrade} {sc}</option>)}
            </select>
          </div>
          <input ref={fileRef} data-testid="excel-import-file-input" type="file" accept=".xlsx,.xlsm" className={inputCls} />
          <button data-testid="excel-import-submit-btn" onClick={doImport} disabled={busy} className={`${btnTurq} w-full justify-center`}>
            {busy ? "جارٍ الاستيراد …" : "بدء الاستيراد"}
          </button>
        </div>
      </Modal>

      <Modal open={!!report} onClose={() => setReport(null)} title="تقرير الاستيراد الفوري" wide testid="import-report-modal">
        {report && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-xl bg-turq-100 dark:bg-surface2 p-4">
                <div data-testid="import-imported-count" className="font-head font-black text-2xl text-turq-900 dark:text-turq-300">{report.imported}</div>
                <div className="text-[11px] text-mutext">سجل مستورد</div>
              </div>
              <div className="rounded-xl bg-beige-600 dark:bg-surface2 p-4">
                <div data-testid="import-duplicates-count" className="font-head font-black text-2xl text-turq-900 dark:text-turq-300">{report.duplicates}</div>
                <div className="text-[11px] text-mutext">سجل مكرر (لم يُحتسب)</div>
              </div>
              <div className="rounded-xl bg-coral-100 dark:bg-surface2 p-4">
                <div data-testid="import-rejected-count" className="font-head font-black text-2xl text-coral-900 dark:text-coral-300">{report.rejected.length}</div>
                <div className="text-[11px] text-mutext">سجل مرفوض</div>
              </div>
            </div>
            {report.rejected.length > 0 && (
              <div className="max-h-56 overflow-auto rounded-xl border border-brd">
                <table className="w-full text-xs">
                  <thead><tr className="bg-coral-100 text-coral-900"><th className="px-3 py-2 text-right">الصف</th><th className="px-3 py-2 text-right">سبب الرفض</th></tr></thead>
                  <tbody>
                    {report.rejected.map((r, i) => (
                      <tr key={r.row} data-testid={`rejected-row-${r.row}`} className="border-t border-brd/50">
                        <td className="px-3 py-2 font-bold">{r.row}</td>
                        <td className="px-3 py-2">{r.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button data-testid="import-report-close-btn" onClick={() => setReport(null)} className={`${btnPrimary} w-full justify-center`}>تم</button>
          </div>
        )}
      </Modal>
    </div>
  );
}
